import React, { useEffect, useState } from 'react';
import { FormControl, HStack, Input, Switch, VStack } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';
import AmountDropdown from '../dropdowns/Amount';
import PaymentDropdown from '../dropdowns/Payment';
import {
    GiftCardLocationResponse,
    GiftCardPayment,
    i18nFn,
    observer,
    useActivateGiftCard,
    useActivateGiftCardWithPoints,
    useSpringBigStore
} from '@springbig/shared';
import LocationDropdown from '../dropdowns/Location';
import { Caption, CaptionError } from '~/shared/Typography';
import ButtonSpinner from '~/shared/ButtonSpinner';
import Button from '~/shared/Button';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';

const SendToFriend = ({ t }: { t: i18nFn }) => {

    const { member, merchant } = useSpringBigStore();
    const gStyle = useGeneralStyles();

    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState<number>(10);
    const [convertPoints, setConvertPoints] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState<GiftCardPayment | null>(null);
    const [submitError, setSubmitError] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isActivateWithPoints, setIsActivateWithPoints] = useState(false);

    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    const {
        mutate: requestActivateGiftCard,
        error: requestActivateCardError,
        isLoading: activateGiftCardLoading
    } = useActivateGiftCard();

    const {
        mutate: activateWithPoints,
        error: activateWithPointsError,
        isLoading: activateWithPointsLoading,
    } = useActivateGiftCardWithPoints();

    useEffect(() => {

        requestActivateCardError && setSubmitError(t('general.submitError'));
    }, [requestActivateCardError]);

    const handleEmailChange = (text: string) => {

        setEmail(text);
        setIsValidEmail(emailPattern.test(text));
    };

    const isSubmitEnabled =
        (paymentMethod || isActivateWithPoints ) &&
        (amount || convertPoints) &&
        !activateGiftCardLoading &&
        !activateWithPointsLoading;

    const handleSubmit = () => {

        setIsValidEmail(emailPattern.test(email));

        if (emailPattern.test(email) && isSubmitEnabled) {
            if (isActivateWithPoints) {
                activateWithPoints({
                    auto_reload: false,
                    auto_reload_amount: '20.00',
                    purchase_location: '0',
                    member_id: member?.id,
                    friends_email: email,
                    convert_points: convertPoints,
                }, {
                    onSuccess: (_) => {

                        observer.emit('sent-giftcard', { email, amount: (convertPoints / Number(merchant.dollars_per_point)).toString() });
                    }
                })
            } else {
                requestActivateGiftCard({
                    balance: amount.toString(),
                    auto_reload: false,
                    auto_reload_amount: '20.00',
                    purchase_location: '0',
                    gift_card_payment_id: paymentMethod?.id || 0,
                    member_id: member?.id.toString() || '',
                    friends_email: email
                }, {
                    onSuccess: (_) => {

                        observer.emit('sent-giftcard', { email, amount: amount.toString() });
                    }
                });
            }
        }
    };

    return (
        <VStack space={5}>
            <Caption alignSelf='center' fontSize={24}>
                { t('giftCard.send.title', [merchant?.gift_card_alias]) }
            </Caption>
            {
                merchant.points_cash_conversion_enabled &&
                merchant.create_gift_card_with_points &&
                member.balance >= parseFloat(merchant.dollars_per_point) &&
                <HStack justifyContent={'space-between'}>
                    <Caption fontSize={18} fontWeight={300}>
                        { t('giftCard.addFunds.fundWithPoints', [merchant.gift_card_alias]) }
                    </Caption>
                    <Switch
                        onTrackColor={gStyle.button_background}
                        isChecked={isActivateWithPoints}
                        onToggle={() => setIsActivateWithPoints(!isActivateWithPoints)}
                    />
                </HStack>
            }
            { !isActivateWithPoints && merchant && !merchant.hide_gift_card_payments &&
                <VStack>
                    <Caption fontSize={18} fontWeight={300}>
                        { t('general.amount') }
                    </Caption>
                    <AmountDropdown
                        t={t}
                        amount={amount}
                        setAmount={setAmount}
                    />
                </VStack>
            }
            { isActivateWithPoints &&
                <PointsConverterField t={t} onChange={(value) => setConvertPoints(value)} />
            }
            <FormControl isRequired isInvalid={!isValidEmail}>
                <Caption fontSize={18} fontWeight={300} mb={1}>
                    { t('giftCard.send.form.email.title') }
                </Caption>
                <Input
                    placeholder={t('giftCard.send.form.email.title')}
                    onChangeText={handleEmailChange}
                    value={email}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    borderWidth={0}
                    borderBottomWidth={0.5}
                    borderBottomColor={gStyle.text}
                />
                { !isValidEmail &&
                    <CaptionError>
                        { t('giftCard.send.form.email.error') }
                    </CaptionError>
                }
            </FormControl>
            { !isActivateWithPoints &&
                <VStack>
                    <Caption fontSize={18} fontWeight={300}>
                        { t('general.payment') }
                    </Caption>
                    <PaymentDropdown
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        t={t}
                    />
                </VStack>
            }
            { member?.customer_gift_card_fee && member?.customer_gift_card_fee !== '$0.00' &&
                <Caption textAlign='center'>
                    { t('giftCard.addFunds.conditions.fees', [member?.customer_gift_card_fee]) }
                </Caption>
            }
            {
                (
                    requestActivateCardError ||
                    requestActivateCardError ||
                    activateWithPointsError
                ) &&
                <CaptionError>
                    {
                        (requestActivateCardError as any)?.data?.message ||
                        (requestActivateCardError as any)?.data?.message ||
                        (activateWithPointsError as any)?.data?.message
                    }
                </CaptionError>
            }
            {
                submitError &&
                (
                    !requestActivateCardError ||
                    !requestActivateCardError ||
                    !activateWithPointsError
                ) &&
                <CaptionError>
                    { submitError }
                </CaptionError>
            }
            <Button
                onPress={handleSubmit}
                backgroundColor={gStyle.button_background}
                borderRadius={10}
                isDisabled={!isSubmitEnabled}
            >
                { (activateGiftCardLoading || activateWithPointsLoading) ?
                    <ButtonSpinner />
                :
                    <Caption color={gStyle.button_text} fontSize={20}>
                        { t('general.send') } ${ isActivateWithPoints ? convertPoints / parseFloat(merchant?.dollars_per_point || '0.0') : amount }
                    </Caption>
                }
            </Button>
        </VStack>
    );
};

export default SendToFriend;
